import type { Channel, Format, Post } from '../types';
import type { Account, Asset, Campaign, CampaignGoal, Idea, PlanSlot, WorkspaceId } from './seed';
import { DEMO_TODAY } from './seed';

type IdeaTemplate = [titleTemplate: string, angle: string, format: Format];

const ideaTemplatesByGoal: Record<CampaignGoal, IdeaTemplate[]> = {
  product: [
    ['First sip: {name}', 'A close-up of the first pour, steam and all.', 'Reel'],
    ['How we make it', 'Behind the bar, step by step, in 15 seconds.', 'Reel'],
    ['Pair it with…', 'One cake that goes perfectly with it, chosen by the team.', 'Photo'],
    ['Regulars try it', 'Two guests, one honest opinion each.', 'Carousel'],
    ['Last chance this week', 'A friendly reminder before the weekend.', 'Photo'],
  ],
  season: [
    ['The season’s first table', 'Window light, a warm cup and the new menu card.', 'Photo'],
    ['Three things to try', 'A carousel with the season’s favourites.', 'Carousel'],
    ['From our kitchen', 'The baker shows how the seasonal cake is made.', 'Reel'],
    ['Cosy corner', 'A slow, quiet morning shot with an invitation.', 'Photo'],
    ['Ask the barista', 'One question from a guest, answered on camera.', 'Reel'],
  ],
  event: [
    ['Save the date', 'Clear date and time, with one photo from the last time.', 'Photo'],
    ['What to expect', 'Three short reasons to come, in a carousel.', 'Carousel'],
    ['Tonight!', 'Same-day reminder with the start time.', 'Story'],
    ['Thank you for coming', 'The best moments, posted the day after.', 'Carousel'],
  ],
  awareness: [
    ['Hello, neighbour', 'Who we are in one photo and two sentences.', 'Photo'],
    ['Meet the team', 'One person, one favourite drink.', 'Photo'],
    ['Where to find us', 'Short walk from the tram stop to the door.', 'Reel'],
    ['Our promise', 'What we never compromise on.', 'Carousel'],
  ],
  'always-on': [
    ['A day at the counter', 'Morning to evening in six photos.', 'Carousel'],
    ['Bean of the month', 'Where it comes from and how it tastes.', 'Photo'],
    ['Small things', 'A detail guests notice but never mention.', 'Photo'],
    ['Team story', 'A short story from someone on the team.', 'Photo'],
  ],
};

const TEMPLATE_PASSES_BEFORE_GIVING_UP = 3;

function ideaFormatForChannel(channel: Channel, templateFormat: Format): Format {
  const tiktokIsVideoOnly = channel === 'TikTok';
  if (tiktokIsVideoOnly) return 'Reel';
  const linkedInHasNoReels = channel === 'LinkedIn' && templateFormat === 'Reel';
  if (linkedInHasNoReels) return 'Photo';
  return templateFormat;
}

export function suggestIdeas(campaign: Campaign, count = 4, salt = Date.now()): Idea[] {
  const goalTemplates = ideaTemplatesByGoal[campaign.goal];
  const takenTitles = new Set(campaign.ideas.map((idea) => idea.title));
  const suggestedIdeas: Idea[] = [];
  const campaignSubject = campaign.name.replace(/ campaign$/i, '');
  const maximumAttempts = goalTemplates.length * TEMPLATE_PASSES_BEFORE_GIVING_UP;
  for (let attempt = 0; suggestedIdeas.length < count && attempt < maximumAttempts; attempt++) {
    const templateIndex = (campaign.ideas.length + attempt) % goalTemplates.length;
    const [titleTemplate, angle, templateFormat] = goalTemplates[templateIndex];
    const namedTitle = titleTemplate.replace('{name}', campaignSubject);
    const passNumber = Math.floor(attempt / goalTemplates.length) + 2;
    const finalTitle = takenTitles.has(namedTitle) ? `${namedTitle} (${passNumber})` : namedTitle;
    if (takenTitles.has(finalTitle)) continue;
    takenTitles.add(finalTitle);
    const channelIndex =
      (suggestedIdeas.length + campaign.ideas.length) % Math.max(1, campaign.channels.length);
    const channel = campaign.channels[channelIndex] ?? 'Instagram';
    suggestedIdeas.push({
      id: `idea-${salt}-${suggestedIdeas.length}`,
      title: finalTitle,
      angle,
      channel,
      format: ideaFormatForChannel(channel, templateFormat),
      status: 'Suggested',
    });
  }
  return suggestedIdeas;
}

export const holdsSlotStatus = (status: Post['status']) =>
  !['Approved · Stopped', 'Rejected', 'Intentionally empty', 'Cancelled'].includes(status);

export function freeDay(
  posts: Post[],
  workspaceId: string,
  channel: Channel,
  from: number,
  to: number
) {
  const firstSchedulableDay = Math.max(from, DEMO_TODAY + 1);
  for (let day = firstSchedulableDay; day <= to; day++) {
    const channelSlotTaken = posts.some(
      (post) =>
        post.workspaceId === workspaceId &&
        post.channel === channel &&
        post.day === day &&
        holdsSlotStatus(post.status)
    );
    if (!channelSlotTaken) return day;
  }
  return undefined;
}

const defaultPublishTimeByChannel: Record<Channel, string> = {
  Instagram: '09:00',
  Facebook: '13:30',
  TikTok: '17:15',
  LinkedIn: '09:00',
};

const polishOpeningByEnglishTemplateTitle: Record<string, string> = {
  'How we make it': 'Tak to robimy',
  'Pair it with…': 'Idealnie pasuje do…',
  'Regulars try it': 'Stali goście próbują',
  'Last chance this week': 'Ostatnia szansa w tym tygodniu',
  'The season’s first table': 'Pierwszy stolik sezonu',
  'Three things to try': 'Trzy rzeczy do spróbowania',
  'From our kitchen': 'Prosto z naszej kuchni',
  'Cosy corner': 'Przytulny kącik',
  'Ask the barista': 'Zapytaj baristę',
  'Save the date': 'Zapisz datę',
  'What to expect': 'Czego się spodziewać',
  'Tonight!': 'Już dziś wieczorem!',
  'Thank you for coming': 'Dziękujemy, że byliście',
  'Hello, neighbour': 'Dzień dobry, sąsiedzie',
  'Meet the team': 'Poznaj nasz zespół',
  'Where to find us': 'Jak do nas trafić',
  'Our promise': 'Nasza obietnica',
  'A day at the counter': 'Dzień za ladą',
  'Bean of the month': 'Kawa miesiąca',
  'Small things': 'Drobiazgi',
  'Team story': 'Historia z zespołu',
  'Holiday opening hours': 'Godziny otwarcia w święta',
  'Plan your holiday coffee': 'Zaplanuj świąteczną kawę',
  'Last days before the break': 'Ostatnie dni przed przerwą',
  'Closed 24–26 December': 'Zamknięte 24–26 grudnia',
  'New Year’s Eve: open 10–14': 'Sylwester: otwarte 10–14',
  'See you on 27 December': 'Do zobaczenia 27 grudnia',
};
const POLISH_DIACRITICS = /[ąćęłńóśźż]/i;

export const looksPolish = (text: string) =>
  POLISH_DIACRITICS.test(text) ||
  /\b(prosz\w*|zrób|zrob|post\s+o|dla\s|jutro|sobot\w*|niedziel\w*|piątek|nowy|nowa|nasz\w*)\b/i.test(
    text
  );
export function polishTitle(title: string): string | undefined {
  if (polishOpeningByEnglishTemplateTitle[title]) return polishOpeningByEnglishTemplateTitle[title];
  const firstSipMatch = /^First sip: (.+)$/.exec(title);
  if (firstSipMatch) {
    const productName = firstSipMatch[1];
    return POLISH_DIACRITICS.test(productName)
      ? `Pierwszy łyk: ${productName}`
      : 'Pierwszy łyk nowości';
  }
  return POLISH_DIACRITICS.test(title) ? title : undefined;
}

type ContentLanguage = 'pl' | 'en';

function defaultLanguageForChannel(channel: Channel): ContentLanguage {
  return channel === 'LinkedIn' ? 'en' : 'pl';
}

function captionLanguage(channel: Channel, campaign: Campaign | undefined): ContentLanguage {
  const channelDefault = defaultLanguageForChannel(channel);
  const campaignLanguages = campaign?.languages;
  if (!campaignLanguages?.length) return channelDefault;
  return campaignLanguages.includes(channelDefault) ? channelDefault : campaignLanguages[0];
}

function publishableFormat(channel: Channel, requestedFormat: Format): Format {
  const allowedFormats = channelFormats[channel as keyof typeof channelFormats];
  if (allowedFormats?.includes(requestedFormat)) return requestedFormat;
  return allowedFormats?.[0] ?? requestedFormat;
}

const endAsSentence = (text: string) => (/[.!?…]$/.test(text) ? text : `${text}.`);

export function draftPost(options: {
  workspaceId: WorkspaceId;
  title: string;
  channel: Channel;
  format: Format;
  day: number;
  campaign?: Campaign;
  accounts: Account[];
  assets: Asset[];
  brief?: string;
  status?: Post['status'];
  language?: 'pl' | 'en';
}): Post {
  const { workspaceId, title, channel, day, campaign, accounts, assets } = options;
  const format = publishableFormat(channel, options.format);
  const businessAccount = accounts.find(
    (account) =>
      account.workspaceId === workspaceId &&
      account.network === channel &&
      !/personal/i.test(account.type)
  );
  const accountName = businessAccount?.name ?? channel;
  const usablePhotos = assets.filter(
    (asset) => asset.workspaceId === workspaceId && asset.image && !asset.archived
  );
  const image = usablePhotos.length
    ? usablePhotos[(title.length + day) % usablePhotos.length].image
    : '';
  const language: ContentLanguage = options.language ?? captionLanguage(channel, campaign);
  const isLipa = workspaceId === 'lipa';
  let caption: string;
  if (language === 'en') {
    const briefFirstSentence = campaign?.brief.split('.')[0] ?? 'Come and see us';
    caption = `${endAsSentence(title)} ${briefFirstSentence}. See you soon!`;
  } else {
    const polishOpening = polishTitle(title);
    const openingSentence = polishOpening ? `${endAsSentence(polishOpening)} ` : '';
    const invitation = isLipa
      ? 'Zapraszamy do Lipy – czekamy na Ciebie przy oknie.'
      : 'Wpadnij do Brzozy – kawa już czeka!';
    caption = `${openingSentence}${invitation}`;
  }
  const workspaceIdPrefix = isLipa ? 'lip' : 'brz';
  const randomSuffix = Math.floor(Math.random() * 1000);
  const campaignSuffix = campaign ? ` · ${campaign.key}` : '';
  return {
    id: `${workspaceIdPrefix}-n${Date.now()}-${randomSuffix}`,
    workspaceId,
    title,
    channel,
    account: accountName,
    status: options.status ?? 'Needs your review',
    day,
    time: defaultPublishTimeByChannel[channel],
    image,
    format,
    caption,
    language,
    campaign: campaign?.key,
    brief: options.brief ?? `${title}${campaignSuffix}`,
    revision: 1,
    history: [{ at: '', by: 'AMY', text: 'Drafted by AMY' }],
    comments: [],
  };
}

export function guessKind(text: string): 'post' | 'ideas' | 'note' {
  if (/\b(ideas?|pomys\w*|brainstorm)\b/i.test(text)) return 'ideas';
  if (
    /\b(post|reel|story|stories|carousel|tiktok|video|wpis\w*|rolk\w*|make|create|prepare|zrób|przygotuj)\b/i.test(
      text
    )
  )
    return 'post';
  return 'note';
}

export function guessChannel(text: string, campaign?: Campaign): Channel {
  const namedChannel = (['Instagram', 'Facebook', 'TikTok', 'LinkedIn'] as Channel[]).find(
    (channel) => new RegExp(channel, 'i').test(text)
  );
  return namedChannel ?? campaign?.channels[0] ?? 'Instagram';
}

const MAXIMUM_TITLE_WORDS = 7;

export function titleFrom(text: string) {
  const topicAfterAbout = /\babout (.+?)(?: for | on | next |\?|\.|$)/i.exec(text)?.[1];
  const requestWithoutPoliteOpening = text
    .replace(/^(please |can you |could you )/i, '')
    .replace(/[?.!]+$/, '');
  const topic = topicAfterAbout ?? requestWithoutPoliteOpening;
  const leadingWords = topic.split(/\s+/).slice(0, MAXIMUM_TITLE_WORDS).join(' ');
  return leadingWords.charAt(0).toUpperCase() + leadingWords.slice(1);
}

type WeekdayPattern = [weekdayName: RegExp, weekdayNumber: number];

const weekdayPatterns: WeekdayPattern[] = [
  [/\b(monday|poniedział\w*)\b/i, 1],
  [/\b(tuesday|wtor\w*)\b/i, 2],
  [/\b(wednesday|środ\w*)\b/i, 3],
  [/\b(thursday|czwart\w*)\b/i, 4],
  [/\b(friday|piąt\w*)\b/i, 5],
  [/\b(saturday|sobot\w*)\b/i, 6],
  [/\b(sunday|niedziel\w*)\b/i, 0],
];
const DEMO_YEAR = 2026;
const DEMO_MONTH_INDEX_NOVEMBER = 10;
const DAYS_IN_WEEK = 7;

export function dayFrom(text: string): number | undefined {
  if (/\b(tomorrow|jutro)\b/i.test(text)) return DEMO_TODAY + 1;
  const mentionedWeekday = weekdayPatterns.find(([weekdayName]) => weekdayName.test(text));
  if (!mentionedWeekday) return undefined;
  const [, weekdayNumber] = mentionedWeekday;
  for (let day = DEMO_TODAY + 1; day <= DEMO_TODAY + DAYS_IN_WEEK; day++) {
    const weekdayOfDay = new Date(DEMO_YEAR, DEMO_MONTH_INDEX_NOVEMBER, day).getDay();
    if (weekdayOfDay === weekdayNumber) return day;
  }
  return undefined;
}

const holidayAnnouncementTitles: [title: string, format: Format][] = [
  ['Holiday opening hours', 'Photo'],
  ['Plan your holiday coffee', 'Carousel'],
  ['Last days before the break', 'Photo'],
  ['Closed 24–26 December', 'Story'],
  ['New Year’s Eve: open 10–14', 'Photo'],
  ['See you on 27 December', 'Photo'],
];

export const channelFormats: Record<'Instagram' | 'Facebook' | 'TikTok' | 'LinkedIn', Format[]> = {
  Instagram: ['Photo', 'Carousel', 'Reel', 'Story'],
  Facebook: ['Photo', 'Carousel', 'Reel', 'Story'],
  TikTok: ['Reel'],
  LinkedIn: ['Photo', 'Carousel', 'PDF'],
};

const DEFAULT_CAMPAIGN_LENGTH_IN_DAYS = 14;
const MINIMUM_SUGGESTED_IDEAS = 4;
const FIXED_IDEA_SALT_FOR_REPEATABLE_PLANS = 1;

export function buildPlan(
  campaign: Pick<
    Campaign,
    'goal' | 'name' | 'start' | 'end' | 'channels' | 'perWeek' | 'ideas' | 'workspaceId'
  > & { template?: string },
  posts: Post[]
): PlanSlot[] {
  const firstDay = Math.max(campaign.start ?? DEMO_TODAY + 1, DEMO_TODAY + 1);
  const lastDay = campaign.end ?? firstDay + DEFAULT_CAMPAIGN_LENGTH_IN_DAYS - 1;
  const campaignDayCount = lastDay - firstDay + 1;
  const postsAtChosenRhythm = Math.round((campaign.perWeek * campaignDayCount) / DAYS_IN_WEEK);
  const availableSlotCount = campaignDayCount * campaign.channels.length;
  const plannedPostCount = Math.max(1, Math.min(postsAtChosenRhythm, availableSlotCount));
  const titlesInStoryOrder =
    campaign.template === 'tpl-announce'
      ? holidayAnnouncementTitles.map(([title, format]) => ({ title, format }))
      : suggestIdeas(
          { ...campaign, ideas: campaign.ideas ?? [] } as Campaign,
          Math.max(plannedPostCount, MINIMUM_SUGGESTED_IDEAS),
          FIXED_IDEA_SALT_FOR_REPEATABLE_PLANS
        ).map((idea) => ({ title: idea.title, format: idea.format }));
  const slots: PlanSlot[] = [];
  const daysBetweenPosts = campaignDayCount / plannedPostCount;
  for (let slotIndex = 0; slotIndex < plannedPostCount; slotIndex++) {
    const channel = campaign.channels[slotIndex % campaign.channels.length];
    const targetDay = Math.floor(firstDay + slotIndex * daysBetweenPosts);
    const slotsAsPlannedPosts = slots.map(
      (slot) =>
        ({
          workspaceId: campaign.workspaceId,
          channel: slot.channel,
          day: slot.day,
          status: 'Planned',
        }) as Post
    );
    const day = freeDay(
      [...posts, ...slotsAsPlannedPosts],
      campaign.workspaceId,
      channel,
      targetDay,
      lastDay
    );
    if (!day) continue;
    slots.push({ day, channel, format: 'Photo', title: '' });
  }
  const byDay = (first: PlanSlot, second: PlanSlot) => first.day - second.day;
  slots.sort(byDay).forEach((slot, calendarIndex) => {
    const pickedTitle = titlesInStoryOrder[Math.min(calendarIndex, titlesInStoryOrder.length - 1)];
    const titlesRanOut = calendarIndex >= titlesInStoryOrder.length;
    slot.title = titlesRanOut ? `${pickedTitle.title} · ${slot.channel}` : pickedTitle.title;
    const allowedFormats = channelFormats[slot.channel];
    slot.format = allowedFormats.includes(pickedTitle.format)
      ? pickedTitle.format
      : allowedFormats[0];
  });
  return slots.sort(byDay);
}
